/**
 * Shared helpers for decomposing a free-form "details document"
 * (artifactDetails / executionDetails) into presence-driven view blocks.
 *
 * The artifact and execution detail APIs both return a polymorphic document
 * with the same known shape (scalar fields + latestSummary /
 * latestProcessorResponse / responses[] / response), so the decomposition and
 * value-formatting logic lives here once and is imported by both mappers.
 */
import { KeyValue } from './paymentDetailTypes';
import { fullTimestamp, titleCase } from './paymentDetailFormat';

/** One captured bank response (details.responses[]); rawResponse is shown in a JSON view. */
export interface ResponseEntryView {
  id: string;
  /** Header line: capturedAt · system · direction (present parts only). */
  heading: string;
  /** rawResponse, pretty-printed when it parses as JSON, else the raw text. */
  rawResponse: string;
}

/** Nested/array keys of the details document that get their own blocks (excluded from scalar detailFields). */
export const STRUCTURED_KEYS = new Set(['latestSummary', 'latestProcessorResponse', 'responses', 'response']);

/** Some producers serialise missing strings as the literal "null". */
export const present = (value?: string | null): string | undefined =>
  value && value !== 'null' ? value : undefined;

/** Display value: the string, or "-" when empty / "null". */
export const dash = (value?: string | null): string => present(value) ?? '-';

/** A single scalar value → display string ("-" when empty); unexpected objects are JSON-stringified. */
export const stringifyScalar = (value: unknown): string => {
  if (value === null || value === undefined || value === 'null') return '-';
  if (typeof value === 'string') return value || '-';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/** A payload (JSON string or object) → pretty JSON; raw text when it is not JSON; "" when absent. */
export const prettyJson = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** Scalar entries of the details document (excludes the structured keys), in document order. */
export const toDetailFields = (doc: Record<string, unknown>): KeyValue[] =>
  Object.entries(doc)
    .filter(([key]) => !STRUCTURED_KEYS.has(key))
    .map(([key, value]) => ({ key, value: stringifyScalar(value) }));

/** KeyValue[] for the present keys of a nested object (skips missing/null/empty). */
export const kvPresent = (obj: Record<string, unknown> | undefined, keys: string[]): KeyValue[] => {
  if (!obj) return [];
  return keys
    .filter(key => obj[key] !== undefined && obj[key] !== null && obj[key] !== 'null' && obj[key] !== '')
    .map(key => ({ key, value: stringifyScalar(obj[key]) }));
};

/** responses[] (ResponseEntry) → view rows, rawResponse pretty-printed for a JSON view. */
export const toResponses = (doc: Record<string, unknown>): ResponseEntryView[] => {
  const responses = doc.responses;
  if (!Array.isArray(responses)) return [];
  return responses.map((entry, index) => {
    const e = (entry ?? {}) as Record<string, unknown>;
    const heading = [
      e.capturedAt ? fullTimestamp(String(e.capturedAt)) : undefined,
      present(e.system as string),
      e.direction ? titleCase(String(e.direction)) : undefined,
    ].filter(Boolean).join(' · ');
    return {
      id: e.id ? String(e.id) : `response-${index}`,
      heading: heading || `Response ${index + 1}`,
      rawResponse: prettyJson(e.rawResponse),
    };
  });
};
